import { DefaultButton, DetailsList, IColumn, Icon, Link, MessageBar, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { EntityAPI, EntityType, IContentHubType, IEntityColumn, IEntityToken, ITemplateTrigger, IUserConfigPackage } from '../../../API';
import TemplateTriggerEditor from './TemplateTriggerEditor';
import { TemplateTriggerEditorState } from './TemplateTriggerEditor';

export class TemplateTriggersState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: TemplateTriggerEditorState;
	public templateFilesUrl: string | undefined;
	public shouldDisplayAddTemplateMessage = false;
	public shouldDisplayResyncMessage = false;
	
	public tokens?: IEntityToken[];
	public listUrl: string = '';
	
	update: (ids: string[]) => void;
	
	items: ITemplateTrigger[] = [];
	availableContentTypes: IContentHubType[] = [];

	constructor(userConfig: IUserConfigPackage,  update:(ids:string[]) => void, listUrl: string	= '') {
		this.userConfig = userConfig;
		this.update = update;
		this.listUrl = listUrl;
		
		this.editState = new TemplateTriggerEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this), listUrl);

		makeAutoObservable(this);
	}
	
	public setListUrl(listUrl: string) {
		this.listUrl = listUrl;
		this.editState.setListUrl(listUrl);
	}

	public setAvailableTokens(tokens: IEntityToken[]) {
		this.tokens = tokens;
		this.editState.setTokens(tokens);
	}
	
	public setAvailableFields(listUrlColumns: IEntityColumn[]) {
		this.editState.setAvailableFields(listUrlColumns);
	}
	
	public async setSelected(ids:string[]) {
		this.loading = true;

		if(ids.length > 0) {
			let api = await this.getApi();
			let profiles = await api.items(ids, null);
			this.setItems(profiles);
		} else {
			this.setItems([]);
		}
	}

	public setAvailableContentTypes(types: IContentHubType[]) {
		this.availableContentTypes = types;
	}

	private async getApi() : Promise<EntityAPI<ITemplateTrigger>> {
		return await EntityAPI.fromEntityType(
			EntityType.TemplateTrigger,
			this.userConfig,
		) as EntityAPI<ITemplateTrigger>;
	}

	setItems(items: ITemplateTrigger[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: ITemplateTrigger) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {
		this.edit({
			
			Id: '',
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
			CachedTemplateFile: '',
			TemplateUrl: '',
			TriggerExpression: '',
			TemplateProvisionIncludeSharePointIfTeams: false,
			TemplateProvisionIncludeFiles: false,
			TemplateProvisionIncludeListItems: false,
			OnCompletionTriggerActionIds: [],
			TemplateApprovalListItemId: ''
		});
	}

	public async save(original:ITemplateTrigger, updated:ITemplateTrigger) : Promise<void> {
		
		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:ITemplateTrigger) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(profile:ITemplateTrigger) {
		this.items = this.items.filter(p => p !== profile);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		this.update(this.items.map(p => p.Id));
	}
}

function TemplateTriggers({ state }: { state: TemplateTriggersState }) {

	let triggerName = (trigger:ITemplateTrigger) => {
		return trigger.TemplateUrl || 'No template specified';
	};


	let columns: IColumn[] = [
		{
			key: 'resource',
			name: 'Template Resource',
			minWidth: 250,
			maxWidth: 250,
			onRender: (item: ITemplateTrigger) => <Link title={item.TemplateUrl} onClick={() => {
				state.edit(item);
			}}>{triggerName(item)}</Link>,
		},
		{
			key: 'expression',
			name: '',
			minWidth: 40,
			onRender: (item: ITemplateTrigger) => <span>
				<span title={item.TriggerExpression}>{item.TriggerExpression}</span>
			</span>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 60,
			maxWidth: 60,
			onRender: (item: ITemplateTrigger) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}
	
	return (
		<>
			<>
				{state.editState && <TemplateTriggerEditor state={state.editState} /> }

				{state.items.length === 0 ?
					<MessageBar>No templates have been configured. Click 'Add template' to add a template URL.</MessageBar>
					:
					<></>
				}
				
				{(state?.items && state.items.length) ?
					<DetailsList isHeaderVisible={false} selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
			</>

			<div style={{textAlign: 'right', marginTop:20}}>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add template</DefaultButton>
			</div>
		</>
	);
}

export default observer(TemplateTriggers);
